import type { RouteRecordRaw } from 'vue-router';
import { FeatureFlags } from '@/plugins/launchDarkly/featureFlags';

export const distributionRoutes: RouteRecordRaw[] = [
  {
    path: '/distribution',
    name: 'Distribution',
    redirect: { name: 'MyVaultAssets' },
    meta: {
      requiresAuth: true,
      navigation: {
        label: 'Distribution',
        icon: 'dollar-icon.svg', // Changed from distribution-icon.svg to existing dollar-icon.svg
        order: 2,
        showInMenu: true,
        permissions: {
          rbacRoles: ['distributionnav-read']
        }
      }
    },
    children: [
      {
        path: 'myvault/uploads/',
        name: 'MyVaultUploads',
        component: () => import("@/views/my_vault/uploads/Uploads.vue"),
        props: {
          actionPermissions: ["insertkeys-write"],
          viewPermissions: ["insertionslist-read", "insertionslist-all"],
        },
        meta: {
          title: "My Vault - Insertions",
          requiresAuth: true,
          navigation: {
            parent: 'Distribution',
            parentIcon: 'dollar-icon.svg',
            label: 'Insertions',
            order: 99,
            showInMenu: false,
            permissions: {
              viewPermissions: ['insertionslist-read', 'insertionslist-all'],
              actionPermissions: ['insertkeys-write']
            }
          }
        }
      },
      {
        path: 'myvault/uploads-detail/:id',
        name: 'MyVaultUploadsDetail',
        component: () =>
          import("@/views/my_vault/uploads-detail/UploadsDetail.vue"),
        props: { viewPermissions: ["insertionslist-read"] },
        meta: {
          title: "My Vault - Insertion Details",
          requiresAuth: true,
        }
      },
      {
        path: 'myvault/inventory/',
        name: 'MyVaultAssets',
        component: () => import("@/views/my_vault/assets/Assets.vue"),
        props: {
          actionPermissions: ["sku-all", "sku-delete"],
          viewPermissions: ["inventory-read", "inventory-all"],
          extraPermissions: [
            "sku-write",
            "extractkeys-write",
            "insertkeys-write",
            "transferkeys-write",
            "reportinventory-write",
            "report-write",
            "extractrevokedkeys-write",
            "extractinventorykeys-write",
            "acceptkeytransfer-write",
            "reportfinance-write",
            "reportkeybykey-write",
          ],
        },
        meta: {
          title: "My Vault - Inventory",
          requiresAuth: true,
          navigation: {
            parent: 'Distribution',
            parentIcon: 'dollar-icon.svg',
            label: 'My Vault',
            order: 1,
            showInMenu: true,
            permissions: {
              rbacRoles: ['inventory-read', 'inventory-all'],
              ownerRoles: ['Publisher'],
              actionPermissions: ['sku-all', 'sku-delete'],
              viewPermissions: ['inventory-read', 'inventory-all']
            }
          }
        }
      },
      /*{
        path: 'myvault/transaction-detail/:id',
        name: 'MyVaultTransactionDetail',
        component: () =>
          import("@/views/my_vault/transaction-detail/TransactionDetail.vue"),
        props: {
          actionPermissions: ["sku-all", "sku-delete"],
          viewPermissions: ["inventory-read", "inventory-all"],
          extraPermissions: [
            "report-write",
            "extractkeys-write",
            "insertkeys-write",
            "transferkeys-write",
            "extractrevokedkeys-write",
            "extractinventorykeys-write",
            "acceptkeytransfer-write",
            "reportfinance-write",
            "reportkeybykey-write",
          ],
        },
        meta: {
          title: "My Vault - Transactions",
          requiresAuth: true,
        }
      },*/
      {
        path: 'myvault/transfers/stockpool-out/',
        name: 'MyVaultStockpoolOut',
        component: () =>
          import("@/views/my_vault/stockpool-out/StockPoolOut.vue"),
        props: {
          actionPermissions: ["stockpool-all", "stockpool-write"],
          viewPermissions: ["stockpool-read", "stockpool-all"],
        },
        meta: {
          title: "My Vault - Stockpool",
          requiresAuth: true,
        }
      },
      {
        path: 'myvault/transfers/stockpool-in/',
        name: 'MyVaultStockpoolIn',
        component: () => import("@/views/my_vault/stockpool-in/StockPoolIn.vue"),
        props: {
          actionPermissions: ["stockpool-all", "stockpool-write"],
          viewPermissions: ["stockpool-read", "stockpool-all"],
        },
        meta: {
          title: "My Vault - Stockpool",
          requiresAuth: true,
        }
      },
      {
        path: 'myvault/shared-assets/',
        name: 'MyVaultSharedAssets',
        component: () =>
          import("@/views/my_vault/shared-assets/SharedAssets.vue"),
        props: { viewPermissions: ["sharedskus-read"] },
        meta: {
          title: "My Vault - Assets",
          requiresAuth: true,
          navigation: {
            parent: 'Distribution',
            parentIcon: 'dollar-icon.svg',
            label: 'My Vault',
            order: 1,
            showInMenu: true,
            permissions: {
              rbacRoles: ['sharedskus-read', 'sharedskus-all'],
              ownerRoles: ['Retailer'],
              viewPermissions: ['sharedskus-read']
            }
          }
        }
      },
      {
        path: 'myvault/transfers/outbox/',
        name: 'MyVaultOutbox',
        component: () => import("@/views/my_vault/outbox/Outbox.vue"),
        props: { viewPermissions: ["transferslist-read"], actionPermissions: [] },
        meta: {
          title: "My Vault - Outbox",
          requiresAuth: true,
        }
      },
      {
        path: 'myvault/transfers/inbox/',
        name: 'MyVaultInbox',
        component: () => import("@/views/my_vault/inbox/Inbox.vue"),
        props: { viewPermissions: ["transferslist-read"], actionPermissions: [] },
        meta: {
          title: "My Vault - Inbox",
          requiresAuth: true,
        }
      },
      {
        path: 'orders/',
        name: 'MyVaultOrders',
        component: () => import("@/views/my_vault/orders/Orders.vue"),
        props: {
          actionPermissions: ["order-all", "ordercancel-write"],
          viewPermissions: ["order-read", "order-all"],
        },
        meta: {
          title: "My Vault - Orders",
          requiresAuth: true,
          navigation: {
            parent: 'Distribution',
            parentIcon: 'dollar-icon.svg',
            label: 'Orders',
            order: 4,
            showInMenu: true,
            permissions: {
              rbacRoles: ['order-read', 'order-all'],
              ownerRoles: ['Publisher', 'Retailer'],
              viewPermissions: ['order-read', 'order-all'],
              actionPermissions: ['order-all', 'ordercancel-write']
            }
          }
        }
      },
      {
        path: 'orders/:id',
        name: 'MyVaultOrderDetails',
        component: () =>
          import("@/views/my_vault/order-details/OrderDetails.vue"),
        props: {
          actionPermissions: ["order-all", "ordercancel-write"],
          viewPermissions: ["order-read", "order-all"],
        },
        meta: {
          title: "My Vault - Order Details",
          requiresAuth: true,
        }
      },
      {
        path: 'pricing/',
        name: 'FinancePriceLists',
        component: () => import("@/views/finance/price-lists/PriceLists.vue"),
        props: {
          actionPermissions: [
            "pricelist-all",
            "pricelist-delete",
            "pricelist-write",
          ],
          viewPermissions: ["pricelist-read", "pricelist-all"],
        },
        meta: {
          title: "Finance - Price Lists",
          requiresAuth: true,
          navigation: {
            parent: 'Distribution',
            parentIcon: 'dollar-icon.svg',
            label: 'Pricing',
            order: 2,
            showInMenu: true,
            permissions: {
              rbacRoles: ['pricelist-read', 'pricelist-all'],
              ownerRoles: ['Publisher', 'Retailer'],
              viewPermissions: ['pricelist-read', 'pricelist-all'],
              actionPermissions: ['pricelist-all', 'pricelist-delete', 'pricelist-write']
            }
          }
        }
      },
      {
        path: 'pricing/price-lists-edit/:id',
        name: 'FinancePriceListsEdit',
        component: () =>
          import("@/views/finance/price-lists-edit/PriceListsEdit.vue"),
        props: {
          actionPermissions: [
            "pricelist-all",
            "pricelist-delete",
            "pricelist-write",
          ],
          viewPermissions: ["pricelist-read", "pricelist-all"],
          extraPermissions: ["pricelist-write", "pricelist-read"],
        },
        meta: {
          title: "Finance - Edit Price List",
          requiresAuth: true,
        }
      },
      {
        path: 'pricing/price-lists-add/',
        name: 'FinancePriceListsAdd',
        component: () =>
          import("@/views/finance/price-lists-add/PriceListsAdd.vue"),
        props: { viewPermissions: ["pricelist-read"] },
        meta: {
          title: "Finance - Add Price Lists",
          requiresAuth: true,
        }
      },
      {
        path: 'pricing/price-lists-copy/:id',
        name: 'FinancePriceListsCopy',
        component: () =>
          import("@/views/finance/price-lists-copy/PriceListsCopy.vue"),
        props: { viewPermissions: ["pricelist-read"] },
        meta: {
          title: "Finance - Copy Price List",
          requiresAuth: true,
        }
      },
      {
        path: 'promotions/',
        name: 'FinancePromotions',
        component: () => import("@/views/finance/promotions/Promotions.vue"),
        props: {
          actionPermissions: [
            "promotion-all",
            "promotion-write",
            "promotion-delete",
          ],
          viewPermissions: ["promotion-read", "promotion-all"],
        },
        meta: {
          title: "Finance - Promotions",
          requiresAuth: true,
          navigation: {
            parent: 'Distribution',
            parentIcon: 'dollar-icon.svg',
            label: 'Promotions',
            order: 3,
            showInMenu: true,
            permissions: {
              rbacRoles: ['promotion-read', 'promotion-all'],
              ownerRoles: ['Publisher', 'Retailer'],
              viewPermissions: ['promotion-read', 'promotion-all'],
              actionPermissions: ['promotion-all', 'promotion-write', 'promotion-delete']
            }
          }
        }
      },
      {
        path: 'promotions/promotions-edit/:id',
        name: 'FinancePromotionsEdit',
        component: () =>
          import("@/views/finance/promotions-edit/PromotionsEdit.vue"),
        props: {
          actionPermissions: [
            "promotion-all",
            "promotion-write",
            "promotion-delete",
          ],
          viewPermissions: ["promotion-read", "promotion-all"],
        },
        meta: {
          title: "Finance - Edit Promotion",
          requiresAuth: true,
        }
      },
      {
        path: 'promotions/promotions-add/',
        name: 'FinancePromotionsAdd',
        component: () =>
          import("@/views/finance/promotions-add/PromotionsAdd.vue"),
        props: { viewPermissions: ["promotion-read"] },
        meta: {
          title: "Finance - Add Promotion",
          requiresAuth: true,
        }
      },
      {
        path: 'promotions/promotions-copy/:id',
        name: 'FinancePromotionsCopy',
        component: () =>
          import("@/views/finance/promotions-copy/PromotionsCopy.vue"),
        props: { viewPermissions: ["promotion-read"] },
        meta: {
          title: "Finance - Copy Promotion",
          requiresAuth: true,
        }
      },
      {
        path: 'reports/',
        name: 'ReportList',
        component: () => import("@/views/finance/report-list/ReportList.vue"),
        props: {
          actionPermissions: ["report-all", "report-write"],
          viewPermissions: ["report-read", "report-all"],
          extraPermissions: ["reportkeybykey-write", "reportfinance-write", "reportorderpayment-write"],
        },
        meta: {
          title: "Finance - Report List",
          requiresAuth: true,
          navigation: {
            parent: 'Distribution',
            parentIcon: 'dollar-icon.svg',
            label: 'Reports',
            order: 5,
            showInMenu: true,
            permissions: {
              rbacRoles: ['report-read', 'report-all'],
              ownerRoles: ['Publisher', 'Retailer'],
              viewPermissions: ['report-read', 'report-all'],
              actionPermissions: ['report-all', 'report-write']
            }
          }
        }
      },
      {
        path: 'reports/scheduled/',
        name: 'ReportScheduleList',
        component: () => import("@/views/finance/report-schedule-list/ReportScheduleList.vue"),
        props: {
          actionPermissions: ["report-all", "report-write"],
          viewPermissions: ["report-read", "report-all"],
          extraPermissions: ["reportkeybykey-write", "reportfinance-write"],
        },
        meta: {
          title: "Finance - Report Schedules",
          requiresAuth: true,
          navigation: {
            parent: 'Distribution',
            parentIcon: 'dollar-icon.svg',
            label: 'Report Schedules',
            order: 6,
            showInMenu: false,
            featureFlag: FeatureFlags.REPORT_SCHEDULES,
            permissions: {
              rbacRoles: ['report-read', 'report-all'],
              ownerRoles: ['Publisher', 'Retailer'],
              viewPermissions: ['report-read', 'report-all'],
              actionPermissions: ['report-all', 'report-write']
            }
          }
        }
      },
    ]
  }
];